/**
 * Компонент для отображения группы условий Query Builder
 * Рекурсивный компонент - может содержать другие группы
 */

import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Button, Dropdown, Empty, Select, Space } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";

import { QueryBuilderStore } from "../../QueryBuilderStore";
import {
  ZGroupNode,
  ZLogicalOperator,
  ZNodePath,
  ZQueryBuilderNode,
} from "../../types";
import {
  defaultLogicalOperator,
  logicalOperator,
  logicalOperatorOptions,
} from "../../types/ZQueryBuilderConstants";
import { CriterionItem } from "../CriterionItem/CriterionItem";
import * as styles from "./GroupItem.module.less";

// ─── Sub-components ───────────────────────────────────────────────────────────

type PropsGroupHeader = {
  op: ZLogicalOperator;
  canDelete: boolean;
  onOperatorChange: (value: ZLogicalOperator) => void;
  onDelete: () => void;
};

const GroupHeader: React.FC<PropsGroupHeader> = ({
  op,
  canDelete,
  onOperatorChange,
  onDelete,
}) => (
  <div className={styles.groupHeader}>
    <Space size="small">
      <Select
        value={op}
        onChange={onOperatorChange}
        className={styles.operatorSelector}
        options={logicalOperatorOptions}
      />
      {canDelete && (
        <Button
          size="small"
          icon={<DeleteOutlined />}
          onClick={onDelete}
          title="Удалить группу"
        />
      )}
    </Space>
  </div>
);

type PropsGroupFooter = {
  onAddCriterion: () => void;
  onAddAndGroup: () => void;
  onAddOrGroup: () => void;
};

const GroupFooter: React.FC<PropsGroupFooter> = ({
  onAddCriterion,
  onAddAndGroup,
  onAddOrGroup,
}) => {
  const menuItems: MenuProps["items"] = [
    {
      key: "and-group",
      label: "Добавить группу AND",
      onClick: onAddAndGroup,
    },
    { key: "or-group", label: "Добавить группу OR", onClick: onAddOrGroup },
  ];

  return (
    <div className={styles.groupFooterActions}>
      <Button
        type="link"
        size="small"
        icon={<PlusOutlined />}
        onClick={onAddCriterion}
        className={styles.groupActionButton}
      >
        Добавить условие
      </Button>
      <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
        <Button type="link" size="small" className={styles.groupActionButton}>
          () Добавить группу условий
        </Button>
      </Dropdown>
    </div>
  );
};

// ─── Node renderer ─────────────────────────────────────────────────────────────

type NodeRendererArgs = {
  child: ZQueryBuilderNode;
  childPath: ZNodePath;
  store: QueryBuilderStore;
  depth: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  canDeleteCriterion: boolean;
};

const nodeRendererRegistry: Record<
  ZQueryBuilderNode["type"],
  (args: NodeRendererArgs) => React.ReactElement
> = {
  group: ({ child, childPath, store, depth }) => (
    <GroupItem
      key={child.id}
      group={child as ZGroupNode}
      path={childPath}
      store={store}
      depth={depth + 1}
    />
  ),
  criterion: ({
    child,
    childPath,
    store,
    canMoveUp,
    canMoveDown,
    canDeleteCriterion,
  }) => (
    <CriterionItem
      key={child.id}
      criterion={child as Extract<ZQueryBuilderNode, { type: "criterion" }>}
      path={childPath}
      store={store}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
      canDelete={canDeleteCriterion}
      onWrapInGroup={() =>
        store.wrapCriterionInGroup(childPath, defaultLogicalOperator)
      }
    />
  ),
};

// ─── GroupItem ─────────────────────────────────────────────────────────────────

interface PropsGroupItem {
  group: ZGroupNode;
  path: ZNodePath;
  store: QueryBuilderStore;
  depth: number;
  canDelete?: boolean;
}

export const GroupItem: React.FC<PropsGroupItem> = observer(
  ({ group, path, store, depth, canDelete = true }) => {
    const canDeleteCriterion = canDelete || store.criteriaCount > 1;
    const childrenMoveCapabilities = store.getChildrenMoveCapabilities(path);

    const handleOperatorChange = (value: ZLogicalOperator) =>
      store.setGroupOperator(path, value);
    const handleDelete = () => canDelete && store.removeGroup(path);
    const handleAddCriterion = () => store.addCriterion(path);
    const handleAddAndGroup = () =>
      store.addGroup(path, defaultLogicalOperator);
    const handleAddOrGroup = () => store.addGroup(path, logicalOperator.OR);

    return (
      <div
        className={styles.groupItem}
        style={{ marginLeft: depth > 0 ? "24px" : undefined }}
      >
        <GroupHeader
          op={group.op}
          canDelete={canDelete}
          onOperatorChange={handleOperatorChange}
          onDelete={handleDelete}
        />

        <div className={styles.groupChildren}>
          {group.items.length === 0 ? (
            <Empty
              description="Нет условий"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              className={styles.emptyGroup}
            />
          ) : (
            group.items.map((child, index) => {
              const childPath = [...path, index];
              const moveCapabilities = childrenMoveCapabilities[index] ?? {
                canMoveUp: false,
                canMoveDown: false,
              };

              const itemNode = nodeRendererRegistry[child.type]({
                child,
                childPath,
                store,
                depth,
                canMoveUp: moveCapabilities.canMoveUp,
                canMoveDown: moveCapabilities.canMoveDown,
                canDeleteCriterion,
              });

              if (index === 0) {
                return itemNode;
              }

              return (
                <React.Fragment key={`item-wrap-${child.id}`}>
                  <div className={styles.logicalDivider}>
                    <span>{group.op}</span>
                  </div>
                  {itemNode}
                </React.Fragment>
              );
            })
          )}
        </div>

        <GroupFooter
          onAddCriterion={handleAddCriterion}
          onAddAndGroup={handleAddAndGroup}
          onAddOrGroup={handleAddOrGroup}
        />
      </div>
    );
  },
);
